import { useState, useCallback, useEffect } from "react";
import { type MeetingNote, type NoteColor } from "../types/note";
import { getLastFetchedMeeting, fetchMeetingDetails } from "@/features/history/services/meetingService";
import { updateMeetingNotes } from "../services/notesService";
import { toast } from "react-hot-toast";

const sortNotesNewestFirst = (arr: MeetingNote[]): MeetingNote[] => {
  return [...arr].sort((a, b) => {
    const tA = new Date(a.created_at || a.timestamp || 0).getTime();
    const tB = new Date(b.created_at || b.timestamp || 0).getTime();
    if (tA && tB) return tB - tA;
    return 0;
  });
};

export function useMeetingNotes(
  translatorServiceUrl: string,
  currentMeetingId: string | null,
  recordingDuration: string
) {
  const [notes, setNotes] = useState<MeetingNote[]>([]);
  
  // Persist notes panel open state
  const [isNotesOpen, setIsNotesOpen] = useState(() => {
    try {
      return localStorage.getItem("kira_is_notes_open") === "true";
    } catch {
      return false;
    }
  });

  const toggleNotes = useCallback(() => {
    setIsNotesOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("kira_is_notes_open", String(next));
      } catch (err) {
        console.warn("Failed to save notes panel state:", err);
      }
      return next;
    });
  }, []);

  const syncNotesToBackend = useCallback(
    async (updatedNotes: MeetingNote[]) => {
      if (!currentMeetingId) {
        // Persist locally if meeting ID is not available yet (e.g. recording active)
        try {
          localStorage.setItem("kira_temp_notes", JSON.stringify(updatedNotes));
        } catch (err) {
          console.warn("Failed to persist temp notes:", err);
        }
        return;
      }
      if (!translatorServiceUrl) return;
      try {
        await updateMeetingNotes(translatorServiceUrl, currentMeetingId, updatedNotes);
      } catch (err) {
        console.error("Error syncing meeting notes:", err);
        toast.error("Error al guardar las notas en el servidor / Failed to sync notes");
      }
    },
    [currentMeetingId, translatorServiceUrl]
  );

  // Load temp notes on mount if meeting is active but has no ID yet
  useEffect(() => {
    if (!currentMeetingId) {
      try {
        const saved = localStorage.getItem("kira_temp_notes");
        if (saved) {
          setNotes(JSON.parse(saved));
        }
      } catch (err) {
        console.warn("Failed to load temp notes:", err);
      }
    }
  }, [currentMeetingId]);

  // Load backend notes (and hydrate with temp notes if transitioning from null -> active ID)
  useEffect(() => {
    if (!currentMeetingId || !translatorServiceUrl) {
      if (!currentMeetingId) {
        // Keep notes from temp cache
      } else {
        setNotes([]);
      }
      return;
    }
    let isCurrent = true;
    const fetchPastMeetingNotes = async () => {
      // 1. Check for unsaved temp notes in localStorage
      let tempNotes: MeetingNote[] = [];
      try {
        const saved = localStorage.getItem("kira_temp_notes");
        if (saved) {
          tempNotes = JSON.parse(saved);
        }
      } catch (err) {
        console.warn("Error reading temp notes:", err);
      }

      if (tempNotes.length > 0) {
        try {
          // Fetch existing notes from backend to merge
          const json = await fetchMeetingDetails(translatorServiceUrl, currentMeetingId);
          const rawMeeting = json.data?.meeting || json.meeting || json;
          const backendNotes = rawMeeting?.notes || json.data?.notes || json.notes || [];

          // Merge (prevent duplicate IDs)
          const merged = [...tempNotes];
          backendNotes.forEach((bn: MeetingNote) => {
            if (!merged.some((mn) => mn.id === bn.id)) {
              merged.push(bn);
            }
          });

          const sorted = sortNotesNewestFirst(merged);
          await updateMeetingNotes(translatorServiceUrl, currentMeetingId, sorted);

          if (isCurrent) {
            setNotes(sorted);
          }
          localStorage.removeItem("kira_temp_notes");
          return;
        } catch (err) {
          console.error("Failed to sync temp notes to meeting:", err);
        }
      }

      // 2. Normal flow (retrieve from cache or fetch from backend)
      const cached = getLastFetchedMeeting(currentMeetingId);
      if (cached) {
        const rawMeeting = cached.data?.meeting || cached.meeting || cached;
        const extractedNotes = rawMeeting?.notes || cached.data?.notes || cached.notes;
        if (isCurrent && extractedNotes && Array.isArray(extractedNotes)) {
          setNotes(sortNotesNewestFirst(extractedNotes));
          return;
        }
      }

      try {
        const json = await fetchMeetingDetails(translatorServiceUrl, currentMeetingId);
        const rawMeeting = json.data?.meeting || json.meeting || json;
        const extractedNotes = rawMeeting?.notes || json.data?.notes || json.notes;
        if (isCurrent && extractedNotes && Array.isArray(extractedNotes)) {
          setNotes(sortNotesNewestFirst(extractedNotes));
        } else if (isCurrent) {
          setNotes([]);
        }
      } catch (err) {
        console.error("Error fetching past meeting notes:", err);
      }
    };
    fetchPastMeetingNotes();
    return () => {
      isCurrent = false;
    };
  }, [currentMeetingId, translatorServiceUrl]);

  const addNote = useCallback(
    (text: string, color: NoteColor, title?: string) => {
      const now = new Date();
      const currentTimestamp =
        recordingDuration && recordingDuration !== "00:00"
          ? recordingDuration
          : now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      const newNote: MeetingNote = {
        id: `note_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        timestamp: currentTimestamp,
        text,
        title,
        color,
        created_at: now.toISOString(),
      };
      setNotes((prev) => {
        const next = [newNote, ...prev];
        syncNotesToBackend(next);
        return next;
      });
    },
    [recordingDuration, syncNotesToBackend]
  );

  const deleteNote = useCallback(
    (id: string) => {
      setNotes((prev) => {
        const next = prev.filter((n) => n.id !== id);
        syncNotesToBackend(next);
        return next;
      });
    },
    [syncNotesToBackend]
  );

  const exportNotes = useCallback(() => {
    if (notes.length === 0) return;
    const content = notes
      .map((n) => `[${n.timestamp}] ${n.title ? n.title + "\n" : ""}${n.text}`)
      .join("\n\n-------------------------------\n\n");

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notas_reunion_${currentMeetingId || "activa"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [notes, currentMeetingId]);

  const editNote = useCallback(
    (id: string, text: string, title?: string, color?: NoteColor) => {
      setNotes((prev) => {
        const next = prev.map((n) =>
          n.id === id
            ? { ...n, text, title: title !== undefined ? title : n.title, color: color || n.color }
            : n
        );
        syncNotesToBackend(next);
        return next;
      });
    },
    [syncNotesToBackend]
  );

  return {
    notes,
    setNotes,
    isNotesOpen,
    setIsNotesOpen,
    toggleNotes,
    addNote,
    deleteNote,
    editNote,
    exportNotes,
  };
}
